import { theme } from "@src/theme";
import React from "react";
import styled from "styled-components";
import {
  PlaylistDetailsBarInfoTextRow,
  PlaylistDetailsBarInfoValueRow,
  PlaylistDetailsBarPlaylistInfoRow,
} from "./PlaylistDetailsBar.styles";
import { UIButton } from "@src/components/UIButton";
import PreviousIcon from "@assets/img/prev-icon.svg";
import PreviousIconActive from "@assets/img/prev-active.svg";
import NextIcon from "@assets/img/next-icon.svg";
import NextIconActive from "@assets/img/next-active.svg";
import PlayIcon from "@assets/img/play-icon-circle.svg";
import PauseIcon from "@assets/img/pause-button.svg";
import { ITrackStatus } from "@src/redux/slices/trackPlayerSlice";
import Spinner from "@src/components/Spinner";
import { ZoomIn, ZoomOut } from "lucide-react";

const PlaylistWaveFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  height: 155px;
  background-color: ${theme.colors.gray400};
  overflow: hidden;
`;
const TrackDetailsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 50px;
  overflow: hidden;
`;

const TrackDetailsContainerBottom = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 95%;
  height: 50px;
  overflow: hidden;
  align-self: center;
`;

const BottomSide = styled.div`
  flex: 1;
  display: flex;
  align-items: center;

  &:first-child {
    justify-content: flex-start;
  }
  &:last-child {
    justify-content: flex-end;
  }
`;
const WaveFormContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 50px;

  overflow: hidden;
`;

const TimeCode = styled.div`
  opacity: 0.8;
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ZoomButton = styled.button<{ disabled?: boolean }>`
  background: none;
  border: none;
  color: ${theme.colors.gray100};
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ disabled }) => (disabled ? 0.3 : 0.8)};
  padding: 2px;
  transition: opacity 0.15s;

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
  }
`;

type PlaylistWaveFormProps = {
  children: React.ReactNode;
  trackName: string;
  hideControls?: boolean;
  onPreviousClick?: () => void;
  onNextClick?: () => void;
  onPlayClick: () => void;
  onPauseClick: () => void;
  trackStatus?: ITrackStatus;

  isFirstTrackSelected?: boolean;
  isLastTrackSelected?: boolean;
  elapsedTime?: string;
  totalTime?: string;

  showZoomControls?: boolean;
  zoomLevelIndex?: number;
  maxZoomLevelIndex?: number;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
};

const PlaylistWaveForm: React.FC<PlaylistWaveFormProps> = ({
  children,
  hideControls,
  trackName,
  trackStatus,
  onPlayClick,
  onPauseClick,
  onPreviousClick,
  onNextClick,
  isFirstTrackSelected,
  isLastTrackSelected,
  elapsedTime,
  totalTime,
  showZoomControls,
  zoomLevelIndex = 0,
  maxZoomLevelIndex = 0,
  onZoomIn,
  onZoomOut,
}) => {
  return (
    <PlaylistWaveFormContainer>
      <TrackDetailsContainer>
        <PlaylistDetailsBarPlaylistInfoRow>
          <PlaylistDetailsBarInfoTextRow>Track:</PlaylistDetailsBarInfoTextRow>
          <PlaylistDetailsBarInfoValueRow>
            {trackName}
          </PlaylistDetailsBarInfoValueRow>
        </PlaylistDetailsBarPlaylistInfoRow>
      </TrackDetailsContainer>
      <WaveFormContent>{children}</WaveFormContent>
      <TrackDetailsContainerBottom>
        <BottomSide>
          <TimeCode>{elapsedTime}</TimeCode>
        </BottomSide>
        <PlaylistDetailsBarPlaylistInfoRow
          style={{ gap: theme.margins.medium, marginRight: 0 }}
        >
          {!hideControls && (
            <UIButton variant="transparent">
              <img
                onClick={isFirstTrackSelected ? () => {} : onPreviousClick}
                src={isFirstTrackSelected ? PreviousIcon : PreviousIconActive}
                alt="Previous"
              />
            </UIButton>
          )}
          <UIButton
            variant="transparent"
            onClick={() => {
              if (trackStatus === "playing") {
                onPauseClick();
              } else {
                onPlayClick();
              }
            }}
          >
            {trackStatus === "loading" ? (
              <Spinner small />
            ) : (
              <img
                src={trackStatus === "playing" ? PauseIcon : PlayIcon}
                alt="Play"
              />
            )}
          </UIButton>
          {!hideControls && (
            <UIButton disabled={isLastTrackSelected} variant="transparent">
              <img
                onClick={isLastTrackSelected ? () => {} : onNextClick}
                src={isLastTrackSelected ? NextIcon : NextIconActive}
                alt="Next"
              />
            </UIButton>
          )}
        </PlaylistDetailsBarPlaylistInfoRow>
        <BottomSide>
          <TimeCode>{totalTime}</TimeCode>
          {showZoomControls && (
            <ZoomControls style={{ marginLeft: 8 }}>
              <ZoomButton
                disabled={zoomLevelIndex === 0}
                onClick={zoomLevelIndex === 0 ? undefined : onZoomOut}
                title="Zoom out"
              >
                <ZoomOut size={16} />
              </ZoomButton>
              <ZoomButton
                disabled={zoomLevelIndex >= maxZoomLevelIndex}
                onClick={
                  zoomLevelIndex >= maxZoomLevelIndex ? undefined : onZoomIn
                }
                title="Zoom in (up to 1 min view)"
              >
                <ZoomIn size={16} />
              </ZoomButton>
            </ZoomControls>
          )}
        </BottomSide>
      </TrackDetailsContainerBottom>
    </PlaylistWaveFormContainer>
  );
};

export default PlaylistWaveForm;
